import {FlatTreeControl} from '@angular/cdk/tree';
import {Component, Injectable, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {BehaviorSubject, Observable} from 'rxjs';
import {PlatformCommand} from "@shared/commands/command-base";
import {Api} from "@shared/constants/common-field.constants";
import {CommandService} from "@core/services/command.service";
import {FuseSplashScreenService} from "@barebone-fuse-services/splash-screen.service";
import {Router} from "@angular/router";
import {PlatformQuery} from "@shared/models/platform-query/platform-query";
import {map, take, takeUntil} from 'rxjs/operators';
import {PlatformQueryService} from "@core/services/platform-query.service";
import {getUserInfo, hasFeature} from "@shared/store/selectors/security.selector";
import {select, Store} from "@ngrx/store";
import {SecurityState} from "@shared/store/reducers/security.reducer";
import {Subject} from 'rxjs';
import {ExpertServiceFeatures} from "@features/expert-services/expert-service-features";
import {DialogService} from "@shared/services/dialog-service";
import {DialogModel} from "@shared/models/dialog.model";

export class NavigationModel {
  id: string;
  items: NavigationItem[]
}


export class NavigationItem {
  id?: string;
  title: string;
  tooltip?: string;
  type?: string;
  icon?: string;
  children?: NavigationItem[];
  fieldValues?: {};
}

export class NavigationItemFlatNode {
  id?: string;
  title: string;
  tooltip?: string;
  type?: string;
  icon?: string;
  fieldValues?: {};
  level: number;
  expandable: boolean;
}

@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<NavigationItem[]>([]);
  navigation: NavigationModel;

  get data(): NavigationItem[] {
    return this.dataChange.value;
  }

  constructor(private commandService: CommandService,
              private platformQueryService: PlatformQueryService,
              private fuseSplashScreenService: FuseSplashScreenService) {
    // this.initialize();
  }

  initialize(solutionId: string) {
    this.getNavigationItems(solutionId).subscribe(response => {
      // Notify the change.
      this.navigation = response;
      this.triggerUpdate(response.items);
    })
  }

  triggerUpdate(items: NavigationItem[]){
    this.dataChange.next(items);
  }

  getNavigationItems(solutionId: string): Observable<NavigationModel>{
    const query = new PlatformQuery();
    query.api = Api.MarketPlace;
    query.name = 'NavigationDataQuery';
    query.setValue("SolutionId", solutionId);

    return this.platformQueryService.executeQuery(query).pipe(map(response => response.fieldValues['NavigationData']), take(1));

  }


  insertItem(item: NavigationItem) {
    const command = new PlatformCommand("AddNavigationItemCommand", Api.MarketPlace);
    command.setValue("SolutionId", this.navigation.id);
    command.setValue("ParentId", item?.id);

    this.updateNavigation(command);
  }

  addOrbitaxSolution() {
    const command = new PlatformCommand("UpdateNavigationItemCommand", Api.MarketPlace);
    command.setValue("SolutionId", this.navigation.id);
    command.setValue("NavigationItemId", "Solutions")
    // command.setValue("ParentId", "Solutions");//Can be taken on a class or enum.
    command.setValue("Type", "OrbitaxSolution");//Can be taken on a class or enum.

    this.updateNavigation(command);
  }

  addExternalLink() {
    const command = new PlatformCommand("AddNavigationItemCommand", Api.MarketPlace);
    command.setValue("SolutionId", this.navigation.id);
    command.setValue("ParentId", "ExternalLink");//Can be taken on a class or enum.
    command.setValue("Type", "ExternalLink");//Can be taken on a class or enum.

    this.updateNavigation(command);
  }

  updateItem(item: NavigationItem) {
    const command = new PlatformCommand("UpdateNavigationItemCommand", Api.MarketPlace);
    command.setValue("SolutionId", this.navigation.id);
    command.setValue("NavigationItemId", item.id);

    this.updateNavigation(command);
  }

  deleteItem(item: NavigationItem){
    const command = new PlatformCommand("DeleteNavigationItemCommand", Api.MarketPlace);
    command.setValue("SolutionId", this.navigation.id);
    command.setValue("NavigationItemId", item.id);

    this.updateNavigation(command);
  }

  updateNavigation(command: PlatformCommand){
    this.fuseSplashScreenService.show();
    this.commandService.executePlatformCommand(command).subscribe(response => {
      this.fuseSplashScreenService.hide();
      this.navigation = response.fieldValues["NavigationModel"];
      this.triggerUpdate(this.navigation.items);
    })
  }
}


@Component({
    standalone: false,
    selector: 'app-market-place-navigation',
  templateUrl: './market-place-navigation.component.html',
  styleUrls: ['./market-place-navigation.component.scss'],
  providers: [ChecklistDatabase],
  encapsulation: ViewEncapsulation.None
})
export class MarketPlaceNavigationComponent implements OnInit, OnDestroy {
  @Input() solutionId!: string;
  hasFeature$;
  isAdmin;
  isMember;


  MAX_LEVEL = 1;
  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<NavigationItemFlatNode, NavigationItem>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<NavigationItem, NavigationItemFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: NavigationItemFlatNode | null = null;


  treeControl: FlatTreeControl<NavigationItemFlatNode>;

  treeFlattener: MatTreeFlattener<NavigationItem, NavigationItemFlatNode>;

  dataSource: MatTreeFlatDataSource<NavigationItem, NavigationItemFlatNode>;

  expandedNodeSet = new Set<string>()
  private unsubscribe$= new Subject<void>();

  constructor(private router: Router,
              private _database: ChecklistDatabase,
              private commandService: CommandService,
              private securityStore: Store<SecurityState>,
              private dialogService: DialogService) {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren,
    );
    this.treeControl = new FlatTreeControl<NavigationItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    _database.dataChange.subscribe(data => {
      this.rebuildTreeForData(data);
    });
  }



  ngOnInit(): void {
    this._database.initialize(this.solutionId);

    this.hasFeature$ = this.securityStore.pipe(select(hasFeature), takeUntil(this.unsubscribe$));
    this.hasFeature$.subscribe(feature => {
      if (feature) {
        this.isAdmin = feature(ExpertServiceFeatures.FirmAdmin) === true;
        this.isMember = feature(ExpertServiceFeatures.Member) === true;
      }
    });
  }

  getLevel = (node: NavigationItemFlatNode) => node.level;

  isExpandable = (node: NavigationItemFlatNode) => node.expandable;

  getChildren = (node: NavigationItem): NavigationItem[] => node.children;

  isChildlessRoot = (_: number, _nodeData: NavigationItemFlatNode) => _nodeData.level === 0 && !_nodeData.expandable;
  isRootWithChild = (_: number, _nodeData: NavigationItemFlatNode) => _nodeData.level === 0 && _nodeData.expandable;

  isNonRootWithChild = (_: number, _nodeData: NavigationItemFlatNode) => _nodeData.level !== 0 && _nodeData.expandable;

  hasChild = (_: number, _nodeData: NavigationItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: NavigationItemFlatNode) => _nodeData.title === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: NavigationItem, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.title === node.title ? existingNode : new NavigationItemFlatNode();
    flatNode.id = node.id;
    flatNode.title = node.title;
    flatNode.level = level;
    flatNode.expandable = !!node.children?.length;
    flatNode.tooltip = node.tooltip;
    flatNode.type = node.type;
    flatNode.icon = node.icon;
    flatNode.fieldValues = node.fieldValues;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  /* Get the parent node of a node */
  getParentNode(node: NavigationItemFlatNode): NavigationItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  onCLick(node: NavigationItemFlatNode) {
    switch(node.type) {
      case "ExternalLink":
        if(this.isAdmin)
          node.fieldValues["ExternalLink"] = null;
        else if(this.isMember)
          node.fieldValues["RedirectCommand"] = null;

        this.handleClick(node);

        break;

      default:
        if(this.isAdmin)
          break;
        else
          this.handleClick(node);
    }
  }

  handleClick(node: NavigationItemFlatNode){
    if(node.fieldValues["RedirectUrl"]) this.router.navigateByUrl(node.fieldValues['RedirectUrl']);

    if(node.fieldValues["ExternalLink"]) window.open(node.fieldValues["ExternalLink"], node.title, "noopener,noreferrer");

    if(node.fieldValues["RedirectCommand"]){
      this.commandService.executePlatformCommand(node.fieldValues["RedirectCommand"])
        .subscribe(response => {
          this.commandService.handleResponse(response);
          this._database.triggerUpdate(response.fieldValues["NavigationModel"].items)
        });
    }
  }

  addNewItem(node: NavigationItemFlatNode) {
    const navigationItem = this.flatNodeMap.get(node);
    if(node) this.expandedNodeSet.add(node.id);
    this._database.insertItem(navigationItem!);
  }

  addOrbitaxSolution() {
    this._database.addOrbitaxSolution();
  }

  addExternalLink() {
    this._database.addExternalLink();
  }

  deleteItem(node) {
    const navigationItem = this.flatNodeMap.get(node);
    this._database.deleteItem(node);
  }

  updateItem(node) {
    const navigationItem = this.flatNodeMap.get(node);
    this._database.updateItem(navigationItem!);
  }

  onExternalLinkDelete(node){
    this.dialogService.showConfirmDialog("Delete External Link", "Ok", "Cancel", null,
      "Are you sure you want to Delete the External Link").subscribe(res=>{
        if(res.action==="OK"){
          this.deleteItem(node);
        }
    })
  }


  rebuildTreeForData(data: any) {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    this.dataSource.data = [];//To trigger change detection.
    this.dataSource.data = data;
    this.forgetMissingExpandedNodes(this.treeControl, this.expandedNodeSet);
    this.expandNodesById(this.treeControl.dataNodes, Array.from(this.expandedNodeSet));
    // this.expandAllNodes();
  }

  private expandAllNodes() {
    this.treeControl.dataNodes.forEach((node) => {
      this.treeControl.expand(node);
      let parent = this.getParentNode(node);
      while (parent) {
        this.treeControl.expand(parent);
        parent = this.getParentNode(parent);
      }
    });
  }

  private rememberExpandedTreeNodes(
    treeControl: FlatTreeControl<NavigationItemFlatNode>,
    expandedNodeSet: Set<string>
  ) {
    if (treeControl.dataNodes) {
      treeControl.dataNodes.forEach((node) => {
        if (treeControl.isExpandable(node) && treeControl.isExpanded(node)) {
          // capture latest expanded state
          expandedNodeSet.add(node.id);
        }
      });
    }
  }

  private forgetMissingExpandedNodes(
    treeControl: FlatTreeControl<NavigationItemFlatNode>,
    expandedNodeSet: Set<string>
  ) {
    if (treeControl.dataNodes) {
      expandedNodeSet.forEach((nodeId) => {
        // maintain expanded node state
        if (!treeControl.dataNodes.find((n) => n.id === nodeId)) {
          // if the tree doesn't have the previous node, remove it from the expanded list
          expandedNodeSet.delete(nodeId);
        }
      });
    }
  }

  private expandNodesById(flatNodes: NavigationItemFlatNode[], ids: string[]) {
    if (!flatNodes || flatNodes.length === 0) {
      return;
    }
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.id)) {
        this.treeControl.expand(node);
        let parent = this.getParentNode(node);
        while (parent) {
          this.treeControl.expand(parent);
          parent = this.getParentNode(parent);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
